'use client';

import { queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import type {
  ForkHarnessConversationBody,
  HarnessClinic,
  HarnessConversation,
  HarnessConversationSummary,
  HarnessPastCase,
  HarnessRun,
  HarnessScenario,
  HarnessStatus,
  ImportLiveConversationBody,
  StartHarnessRunBody,
} from '@borradh-workspace/api-client/types';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';

const BASE = 'admin-terminal/harness';

export const harnessStatusQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.adminTerminal.harnessStatus(),
    queryFn: () => apiClient.get<HarnessStatus>(`${BASE}/status`),
    staleTime: 15_000,
    retry: false,
  });

export const useHarnessStatus = () => {
  const query = useQuery(harnessStatusQueryOptions());
  return {
    status: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
};

export const useHarnessScenarios = () => {
  const query = useQuery({
    queryKey: queryKeys.adminTerminal.harnessScenarios(),
    queryFn: () => apiClient.get<HarnessScenario[]>(`${BASE}/scenarios`),
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
  return { scenarios: query.data ?? [], isLoading: query.isLoading };
};

export const useHarnessClinics = () => {
  const query = useQuery({
    queryKey: queryKeys.adminTerminal.harnessClinics(),
    queryFn: () => apiClient.get<HarnessClinic[]>(`${BASE}/clinics`),
    staleTime: 60_000,
    retry: false,
  });
  return {
    clinics: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
};

export const useHarnessPastCases = (pack: string | null) => {
  const query = useQuery({
    queryKey: queryKeys.adminTerminal.harnessPastCases(pack ?? ''),
    queryFn: () =>
      apiClient.get<HarnessPastCase[]>(
        `${BASE}/packs/${encodeURIComponent(pack ?? '')}/cases`
      ),
    enabled: Boolean(pack),
    staleTime: 60_000,
    retry: false,
  });
  return { cases: query.data ?? [], isLoading: query.isLoading };
};

export const useHarnessRuns = () => {
  const query = useQuery({
    queryKey: queryKeys.adminTerminal.harnessRuns(),
    queryFn: () => apiClient.get<HarnessRun[]>(`${BASE}/runs`),
    retry: false,
    refetchInterval: (q) =>
      q.state.data?.some((run) => run.status === 'running') ? 2_000 : false,
  });
  return { runs: query.data ?? [], isLoading: query.isLoading };
};

/** Polls every 1.5s while the run is going. */
export const useHarnessRun = (id: string | null) => {
  const query = useQuery({
    queryKey: queryKeys.adminTerminal.harnessRun(id ?? ''),
    queryFn: () => apiClient.get<HarnessRun>(`${BASE}/runs/${id}`),
    enabled: Boolean(id),
    retry: false,
    refetchInterval: (q) =>
      q.state.data?.status === 'running' ? 1_500 : false,
  });
  return { run: query.data ?? null, isLoading: query.isLoading };
};

export const useStartHarnessRun = (options?: {
  onSuccess?: (run: HarnessRun) => void;
}) => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (body: StartHarnessRunBody) =>
      apiClient.post<HarnessRun>(`${BASE}/runs`, body),
    onSuccess: (run) => {
      queryClient.setQueryData(queryKeys.adminTerminal.harnessRun(run.id), run);
      void queryClient.invalidateQueries({
        queryKey: queryKeys.adminTerminal.harnessRuns(),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.adminTerminal.harnessConversations(),
      });
      options?.onSuccess?.(run);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Could not start the run');
    },
  });
  return { startRun: mutation.mutate, isStarting: mutation.isPending };
};

export const useCancelHarnessRun = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (id: string) =>
      apiClient.post<HarnessRun>(`${BASE}/runs/${id}/cancel`, {}),
    onSuccess: (run) => {
      queryClient.setQueryData(queryKeys.adminTerminal.harnessRun(run.id), run);
      void queryClient.invalidateQueries({
        queryKey: queryKeys.adminTerminal.harnessRuns(),
      });
      toast.success('Run stopped');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Could not stop the run');
    },
  });
  return { cancelRun: mutation.mutate, isCancelling: mutation.isPending };
};

/** Saved test conversations on the hosted Harness (empty locally). */
export const useHarnessConversations = (enabled: boolean) => {
  const query = useQuery({
    queryKey: queryKeys.adminTerminal.harnessConversations(),
    queryFn: () =>
      apiClient.get<HarnessConversationSummary[]>(`${BASE}/conversations`),
    enabled,
    staleTime: 10_000,
    retry: false,
  });
  return { conversations: query.data ?? [], isLoading: query.isLoading };
};

/** Load one saved conversation to continue it. */
export const useLoadHarnessConversation = () => {
  const queryClient = useQueryClient();
  return (id: string) =>
    queryClient.fetchQuery({
      queryKey: queryKeys.adminTerminal.harnessConversation(id),
      queryFn: () =>
        apiClient.get<HarnessConversation>(`${BASE}/conversations/${id}`),
      staleTime: 0,
    });
};

const useSaveConversation = () => {
  const queryClient = useQueryClient();
  return (conversation: HarnessConversation) => {
    queryClient.setQueryData(
      queryKeys.adminTerminal.harnessConversation(conversation.id),
      conversation
    );
    void queryClient.invalidateQueries({
      queryKey: queryKeys.adminTerminal.harnessConversations(),
    });
  };
};

/** Take a saved test conversation from one of its messages, as a new one. */
export const useForkHarnessConversation = (options?: {
  onSuccess?: (conversation: HarnessConversation) => void;
}) => {
  const save = useSaveConversation();
  const mutation = useMutation({
    mutationFn: (input: { id: string } & ForkHarnessConversationBody) =>
      apiClient.post<HarnessConversation>(
        `${BASE}/conversations/${input.id}/fork`,
        { messageId: input.messageId }
      ),
    onSuccess: (conversation) => {
      save(conversation);
      toast.success('New conversation from that point');
      options?.onSuccess?.(conversation);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Could not continue from there');
    },
  });
  return { fork: mutation.mutate, isForking: mutation.isPending };
};

/** Continue a real customer conversation as a test, from any point in it. */
export const useImportLiveConversation = (options?: {
  onSuccess?: (conversation: HarnessConversation) => void;
}) => {
  const save = useSaveConversation();
  const mutation = useMutation({
    mutationFn: (body: ImportLiveConversationBody) =>
      apiClient.post<HarnessConversation>(`${BASE}/conversations/import`, body),
    onSuccess: (conversation) => {
      save(conversation);
      options?.onSuccess?.(conversation);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Could not copy that conversation');
    },
  });
  return {
    importConversation: mutation.mutate,
    isImporting: mutation.isPending,
  };
};
